/**
 * Grouped KPI cards — shared by the admin dashboard and the customer detail
 * page. Styling lives in styles.css (.metric, .mpill, .dash-group).
 *
 * A card is white by default, soft-red for urgent action, soft-amber for
 * financial risk. Clickable cards lift on hover and jump to the screen (or
 * filter) that resolves them.
 */

import type { ReactNode } from 'react';

export function MetricCard({
  label, value, meta, tone, onClick, children,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: 'danger' | 'warn' | 'ok';
  onClick?: () => void;
  children?: ReactNode;
}) {
  const cls = ['metric', tone ? `metric--${tone}` : '', onClick ? 'metric--tap' : '']
    .filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
    >
      <div className="metric__label">{label}</div>
      <div className="metric__value">{value}</div>
      {meta ? <div className="metric__meta">{meta}</div> : null}
      {children ? <div className="metric__sub">{children}</div> : null}
    </div>
  );
}

/** A small breakdown pill — Cash vs UPI, a category count, a status word. */
export function Pill({ children, tone }: {
  children: ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info';
}) {
  return <span className={`mpill${tone ? ` mpill--${tone}` : ''}`}>{children}</span>;
}

/** A group heading with an optional "needs attention" count. */
export function GroupHead({ title, count }: { title: string; count?: number }) {
  return (
    <div className="dash-group__head">
      <h2 className="dash-group__title">{title}</h2>
      {count !== undefined && count > 0 && (
        <span className="dash-group__count">{count} need attention</span>
      )}
    </div>
  );
}
